import { SNAKES, LADDERS, INCIDENT_CARDS } from "@/mocks/gameData";

interface Props {
  currentSquare: number;
}

function getSquareNumber(row: number, col: number): number {
  const boardRow = 9 - row;
  if (boardRow % 2 === 0) {
    return boardRow * 10 + col + 1;
  } else {
    return boardRow * 10 + (9 - col) + 1;
  }
}

export default function GameBoard({ currentSquare }: Props) {
  const snakeMap = new Map(SNAKES.map((s) => [s.head, s]));
  const snakeTails = new Set(SNAKES.map((s) => s.tail));
  const ladderMap = new Map(LADDERS.map((l) => [l.bottom, l]));
  const ladderTops = new Set(LADDERS.map((l) => l.top));
  const cardSquares = new Set(INCIDENT_CARDS.map((c) => c.square));

  type SquareType = "snake" | "snake-tail" | "ladder" | "ladder-top" | "card" | "win" | "normal";

  const getSquareType = (sq: number): SquareType => {
    if (sq === 100) return "win";
    if (snakeMap.has(sq)) return "snake";
    if (snakeTails.has(sq)) return "snake-tail";
    if (ladderMap.has(sq)) return "ladder";
    if (ladderTops.has(sq)) return "ladder-top";
    if (cardSquares.has(sq)) return "card";
    return "normal";
  };

  const squareStyles: Record<SquareType, { bg: string; border: string; glow?: string }> = {
    win: {
      bg: "bg-[#00ff88]/20",
      border: "border-[#00ff88]/70",
      glow: "0 0 8px rgba(0,255,136,0.3)",
    },
    snake: {
      bg: "bg-[#ff4444]/12",
      border: "border-[#ff4444]/60",
      glow: "0 0 6px rgba(255,68,68,0.2)",
    },
    "snake-tail": {
      bg: "bg-[#ff4444]/5",
      border: "border-[#ff4444]/20",
    },
    ladder: {
      bg: "bg-[#00ff88]/12",
      border: "border-[#00ff88]/60",
      glow: "0 0 6px rgba(0,255,136,0.2)",
    },
    "ladder-top": {
      bg: "bg-[#00ff88]/5",
      border: "border-[#00ff88]/20",
    },
    card: {
      bg: "bg-[#00d9ff]/8",
      border: "border-[#00d9ff]/40",
      glow: "0 0 6px rgba(0,217,255,0.15)",
    },
    normal: {
      bg: "bg-[#141414]",
      border: "border-white/[0.04]",
    },
  };

  return (
    <div className="w-full">
      {/* Board header */}
      <div className="flex items-center justify-between mb-2 px-0.5">
        <div className="flex items-center gap-2">
          <span className="w-1.5 h-1.5 rounded-full bg-[#00ff88] animate-pulse flex-shrink-0"></span>
          <span className="text-[10px] text-white/25 font-mono uppercase tracking-[0.12em]">INCIDENT_LADDER_BOARD v1.0</span>
        </div>
        <span className="text-[10px] text-white/15 font-mono hidden sm:block">
          {SNAKES.length} snakes · {LADDERS.length} ladders
        </span>
      </div>

      {/* Board grid — uses a square container that fits within available space */}
      <div
        className="w-full rounded-lg overflow-hidden"
        style={{
          border: "1px solid rgba(255,255,255,0.06)",
          background: "#0e0e0e",
          /* Force square aspect ratio that scales with container width */
          aspectRatio: "1 / 1",
        }}
      >
        <div
          className="w-full h-full"
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(10, 1fr)",
            gridTemplateRows: "repeat(10, 1fr)",
            gap: "1px",
            padding: "1px",
          }}
        >
          {Array.from({ length: 10 }, (_, row) =>
            Array.from({ length: 10 }, (_, col) => {
              const sq = getSquareNumber(row, col);
              const type = getSquareType(sq);
              const style = squareStyles[type];
              const isPlayer = sq === currentSquare;
              const snake = snakeMap.get(sq);
              const ladder = ladderMap.get(sq);
              const isEvenRow = (9 - row) % 2 === 0;

              return (
                <div
                  key={`${row}-${col}`}
                  className={`relative flex flex-col items-center justify-center ${style.bg} ${style.border} border transition-all overflow-hidden`}
                  style={{
                    boxShadow: isPlayer
                      ? "0 0 12px rgba(0,217,255,0.6), inset 0 0 8px rgba(0,217,255,0.1)"
                      : style.glow
                      ? style.glow
                      : undefined,
                    background: isPlayer ? "rgba(0,217,255,0.12)" : undefined,
                    minWidth: 0,
                    minHeight: 0,
                  }}
                  title={
                    snake
                      ? `⚠ Snake: ${snake.label} → sq ${snake.tail}`
                      : ladder
                      ? `↑ Ladder: ${ladder.label} → sq ${ladder.top}`
                      : cardSquares.has(sq)
                      ? `? Incident Card`
                      : `Square ${sq}`
                  }
                >
                  {/* Alternating row tint */}
                  {isEvenRow && type === "normal" && (
                    <div className="absolute inset-0 bg-white/[0.012] pointer-events-none" />
                  )}

                  {/* Square number — top-left, scales with container */}
                  <span
                    className="absolute top-[5%] left-[5%] font-mono leading-none select-none"
                    style={{
                      fontSize: "clamp(4px, 1.1cqi, 9px)",
                      color:
                        type === "snake" || type === "snake-tail"
                          ? "rgba(255,68,68,0.45)"
                          : type === "ladder" || type === "ladder-top"
                          ? "rgba(0,255,136,0.45)"
                          : type === "card"
                          ? "rgba(0,217,255,0.45)"
                          : type === "win"
                          ? "rgba(0,255,136,0.7)"
                          : "rgba(255,255,255,0.15)",
                    }}
                  >
                    {sq}
                  </span>

                  {/* Type badge — centered */}
                  {type === "snake" && (
                    <span
                      className="font-black font-mono leading-none"
                      style={{ fontSize: "clamp(5px, 1.3cqi, 10px)", color: "#ff4444" }}
                    >
                      S↓
                    </span>
                  )}
                  {type === "ladder" && (
                    <span
                      className="font-black font-mono leading-none"
                      style={{ fontSize: "clamp(5px, 1.3cqi, 10px)", color: "#00ff88" }}
                    >
                      L↑
                    </span>
                  )}
                  {type === "card" && (
                    <span
                      className="font-black font-mono leading-none"
                      style={{ fontSize: "clamp(5px, 1.3cqi, 10px)", color: "#00d9ff" }}
                    >
                      INC
                    </span>
                  )}
                  {type === "win" && !isPlayer && (
                    <i
                      className="ri-trophy-fill text-[#00ff88]"
                      style={{ fontSize: "clamp(7px, 1.6cqi, 14px)" }}
                    ></i>
                  )}

                  {/* Player token */}
                  {isPlayer && currentSquare > 0 && (
                    <div className="absolute inset-0 flex items-center justify-center z-10">
                      <div
                        className="rounded-full border-2 border-[#00d9ff] bg-[#00d9ff]/30 flex items-center justify-center"
                        style={{
                          width: "clamp(10px, 2.5cqi, 22px)",
                          height: "clamp(10px, 2.5cqi, 22px)",
                          boxShadow: "0 0 12px rgba(0,217,255,0.9), 0 0 4px rgba(0,217,255,0.5)",
                          animation: "pulse 2s infinite",
                        }}
                      >
                        <i
                          className="ri-user-fill text-[#00d9ff]"
                          style={{ fontSize: "clamp(4px, 1.1cqi, 9px)" }}
                        ></i>
                      </div>
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>
      </div>

      {/* Legend */}
      <div className="flex items-center justify-center gap-2 mt-2.5 flex-wrap">
        {[
          { color: "#ff4444", bg: "rgba(255,68,68,0.12)", border: "rgba(255,68,68,0.5)", label: "S↓", text: "Failure Pattern" },
          { color: "#00ff88", bg: "rgba(0,255,136,0.12)", border: "rgba(0,255,136,0.5)", label: "L↑", text: "Reliability Win" },
          { color: "#00d9ff", bg: "rgba(0,217,255,0.08)", border: "rgba(0,217,255,0.4)", label: "INC", text: "Incident Card" },
          { color: "#00d9ff", bg: "rgba(0,217,255,0.2)", border: "#00d9ff", label: "●", text: "You" },
        ].map((item) => (
          <div key={item.text} className="flex items-center gap-1">
            <span
              className="w-5 h-4 rounded flex items-center justify-center font-mono font-bold flex-shrink-0"
              style={{
                fontSize: "6px",
                color: item.color,
                background: item.bg,
                border: `1px solid ${item.border}`,
              }}
            >
              {item.label}
            </span>
            <span className="text-white/25 font-mono text-[10px] whitespace-nowrap">{item.text}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
